<script setup lang="ts">
import model from '@/ts/model.ts';
import AsyncLoad from '@/vue/components/async-load.vue';
import MainNav from '@/vue/components/main-nav/main-nav.vue';
import { computed, ref, watch } from 'vue';
import LoadingSpinner from './components/loading-spinner.vue';
import CookieLawPopup from './components/cookie-law-popup.vue';
import Notifications from './components/notifications.vue';
import InternetConnectionProblem from './components/internet-connection-problem.vue';
import Background from './components/background.vue';
import { appHeaderAdvertDisplayed, globalBlurLogic, setupAtTopIndicator } from '@/ts/management/ui-management';
import { NonCachedViews, viewList } from '@/ts/management/view-navigation-management';
import AppAdvertHeader from './components/app-advert-header.vue';
import MobileGameListShowcase from './components/mobile-game-list-showcase.vue';
import TurnstileDynamicWidget from './components/turnstile-dynamic-widget.vue';
import OpenZendeskBtn from './components/open-zendesk-btn.vue';
import { disposeWatchHandlers } from '@/ts/directives/watch-handlers.ts';
import LobbySection from './components/lobby-sections/lobby-section.vue';
import { isWebReloading } from '@/ts/management/user-management.ts';

const footerDisplayed = computed<boolean>(() => {
	if(model.view == 'gameplay') return false;
	if(!model.isMobile) return true;
	return !model.gameFilter.displayed;
});

const mainNavDisplayed = computed<boolean>(() => {
	return model.view != 'standalone-register';
});

let t = 0;
watch(() => model.globalRequests, () => {
	clearTimeout(t);
	if(model.globalRequests > 0) {
		model.globalLoaderShown = true;
		return;
	}
	t = setTimeout(() => model.globalLoaderShown = false, 200);
}, {immediate: true});

const alwaysShowLobbyVer = localStorage.getItem('show-app-version-test') == 'true';

const vSetupAtTopIndicator = {
	mounted(element: HTMLElement){
		setupAtTopIndicator(element);
	},
};

const footerRendered = ref<boolean>(false);

const vFooterPlaceholder = {
	unmounted: disposeWatchHandlers,
    mounted(element: HTMLElement){
        element.watchHandlers ||= [];

        let t = 0;

        const observer = new IntersectionObserver(entries => {
            clearTimeout(t);
            t = setTimeout(() => {
                for(const entry of entries){
                    if(!entry.isIntersecting) continue;

                    observer.disconnect();
                    footerRendered.value = true;

                    break;
                }
            }, 400);
        }, {rootMargin: '600px 0px'});

        observer.observe(element);

        element.watchHandlers.push(() => {
            clearTimeout(t);
            observer.disconnect();
        });
    },
};
</script>

<template>
	<open-zendesk-btn />

	<div id="at-top-indicator" v-setup-at-top-indicator></div>
	<background />

	<template v-if="model.isMobileFromStart">
		<mobile-game-list-showcase />
	</template>

	<template v-if="model.wof.displayed">
		<template v-if="model.wof.minigameType == 'wof'">
			<async-load 
				:load="() => import('@/vue/components/wof/wof.vue')"
				:global="true"
			/>
		</template>
		<template v-else-if="model.wof.minigameType == 'selection'">
			<async-load 
				:load="() => import('@/vue/components/wof/minigame-selection.vue')"
				:global="true"
			/>
		</template>
	</template>

	<template v-if="model.isMobile ? !model.activeModal : true">
		<template v-if="appHeaderAdvertDisplayed">
			<app-advert-header />
		</template>
		<template v-if="mainNavDisplayed">
			<main-nav />
		</template>
	</template>

	<template v-if="model.activeModal">
		<component :is="`${model.activeModal}-modal`" />
	</template>

	<div 
		:class="{
			blur: globalBlurLogic,
			hidden: model.isMobile ? model.activeModal : false,
		}" 
		v-has-app-header-advert
	id="app" v-mobile>
		<template v-if="model.turnstile?.sitekey">
			<teleport to="body" defer>
				<turnstile-dynamic-widget />
			</teleport>
		</template>
		<template v-if="model.isMobile && model.gameFilter.displayedOnce">
			<div class="mobile-game-filter-wrapper" v-show="model.gameFilter.displayed && model.view != 'gameplay'">
				<async-load
					:load="() => import('@/vue/components/game-filter-mobile.vue')"
				/>
			</div>
		</template>
		<template v-for="view in viewList">
			<template v-if="model.view == view.id || (NonCachedViews.includes(view.id) ? false : model.loadedComponents.includes(view.id + '-view'))">
				<div class="view-wrapper" v-show="model.view == view.id && ((model.isMobile && model.view != 'gameplay') ? !model.gameFilter.displayed : true)" v-mobile>
					<async-load 
						v-bind="{
							...{ 
								load: view.load, 
								bindings: {
									maxHeight: true,
									avoidHeader: true,
									viewId: view.id,
								}, 
								name: view.id + '-view',
							}, 
							...(view.bindings || {}),
						}" 
					/>
				</div>
			</template>
		</template>
		<template v-if="footerDisplayed">
			<template v-if="!footerRendered">
				<lobby-section class="footer-placeholder" v-footer-placeholder :top-color="'neutral_10'" :bottom-color="'neutral_10'">
					<loading-spinner />
				</lobby-section>
			</template>
			<template v-else>
				<async-load 
					:load="() => import('@/vue/components/app-footer/app-footer.vue')"
					:global="false"
				/>
			</template>
		</template>
		<template v-if="!model.isApp">
			<cookie-law-popup />
		</template>
	</div>
	<template v-if="(model.globalLoaderShown || isWebReloading) && !model.turnstile?.sitekey">
		<teleport to="body" defer>
			<div id="global-load-spinner">
				<loading-spinner :max-height="true" :dim="'150'" />
			</div>
		</teleport>
	</template>
	<notifications />
	<internet-connection-problem />
	<template v-if="model.isTest || alwaysShowLobbyVer">
		<div class="lobby-version">{{ model.isTest ? 'pre' : 'prod' }}: {{ model.lobbyVersion }}</div>
	</template>
</template>

<style lang="scss">
@use '@/scss/shared';
@use 'sass:color';
@use 'sass:map';
@use "sass:string";

.footer-placeholder{
	padding: 200px 0px;
}

#at-top-indicator{
	position: absolute;
	width: 100%;
	height: 5px;
	pointer-events: none;
	top:0;
	left:0;
}

@function str-replace($string, $search, $replace: '') {
	$index: string.index($string, $search);

	@if $index {
		@return string.slice($string, 1, $index - 1) + $replace + str-replace(string.slice($string, $index + string.length($search)), $search, $replace);
	}

	@return $string;
}

:root{
	--dev-color: rgb(0, 255, 170);
	--dev-color-prop-name: rgb(0, 172, 143);
	--dev-color-inactive: rgb(0, 97, 65);
	--dev-bg-color: rgb(19, 19, 19);
	@each $site-key, $arr in shared.$theme-colors{
		@each $color-key, $value in $arr {
			$css-name: str-replace($color-key, "_", "-");
			--theme-color-#{$site-key}-#{$css-name}: #{$value};
		}
	}
}

@each $site-key, $arr in shared.$theme-colors{
	html[data-site-key="#{$site-key}"] {
		@each $color-key, $value in $arr {
			$css-name: str-replace($color-key, "_", "-");
			--theme-color-#{$css-name}: var(--theme-color-#{$site-key}-#{$css-name});
		}
	}
}

html, body{
	background-color: #000;
	margin: 0 !important;
	padding: 0 !important;
	color: #fff;
	font-family: Poppins, sans-serif;
	overscroll-behavior: none;
	// @include shared.site() using($site-key){
	// 	@if $site-key == "sky"{
	// 		& img[class*="background"],
	// 		& img[class*="bg"]{
	// 			filter: hue-rotate(75deg) brightness(0.8);
	// 		}
	// 	}
	// }
}

html{
	&:not(.mobile){
		scrollbar-gutter: stable;
		&.active-modal{
			overflow: hidden;
		}
	}
}

#global-load-spinner{
	position: fixed;
	left:0;
	top:0;
	width: 100%;
	height: 100%;
	z-index: 1000;
	opacity: 0;
	animation-fill-mode: forwards;
	animation-duration: .25;
	animation-name: global-load-spinner-display;
	@include shared.site() using($site-key){
		background: linear-gradient(0deg, color.adjust(shared.theme-color("primary_3", $site-key), $alpha: -.5) 0%, rgba(0, 0, 0, 0.5) 100%);
	}
	@keyframes global-load-spinner-display {
		from{
			opacity: 0;
		}
		to{
			opacity: 1;
		}
	}
	& > .loading{
		align-items: center;
		// & > img{
		// 	height: 150px;
		// }
	}
}

#app{
	overflow: hidden;
	&[data-header-advert-displayed]{
		padding-top:60px;
	}
	&:not([data-mobile]){
		& > *,
		& > * > *{
			transition: opacity .25s;
		}
	}
	&.blur{
		& > .mobile-game-filter-wrapper > *,
		& > .view-wrapper > *{
			opacity: .15;
		}
	}
}

.hidden{
	display: none !important;
}

.invisible{
	visibility: hidden !important;
	animation-name: none !important;
}

.scroll-snap{
	&-container{
		scroll-snap-type: x mandatory;
		&::-webkit-scrollbar{
			display: none;
		}
	}
	&-item{
		scroll-snap-align: center;
		scroll-snap-stop: always;
	}
}

.lobby-version{
	position: fixed;
	left:0;
	bottom:0;
	z-index: 100000;
	font-size: 10px;
	background: #000000ab;
	color: #fff;
	pointer-events: none;
	padding: 1px 4px;
	border-radius: 5px;
	margin: 2px;
}
</style>